import {
  buildPageEvent,
  buildIdentifyEvent,
  GeoLocation,
  NinetailedRequestContext,
  NinetailedApiClient,
  SelectedVariantInfo,
  Traits,
} from '@ninetailed/experience.js-shared';
import { v4 as uuid } from 'uuid';
import { RequestCookies } from 'next/dist/compiled/@edge-runtime/cookies';
import { NINETAILED_ANONYMOUS_ID_COOKIE } from 'utils/cookies';
import { logPipelineEvent, now } from 'libs/experimentation/observability';

export type SelectedVariant = { experienceId: string; variantIndex: number };

// Lazy singleton — reuses TCP connections across requests via native fetch.
// Lazy to avoid triggering the constructor at import time (breaks test files
// that transitively import this module without mocking the shared package).
let ntApiClient: NinetailedApiClient;
function getNtApiClient(): NinetailedApiClient {
  if (!ntApiClient) {
    ntApiClient = new NinetailedApiClient({
      clientId: process.env.NEXT_PUBLIC_NINETAILED_API_KEY ?? '',
      environment: 'main',
    });
  }
  return ntApiClient;
}

type SendPagePayload = {
  ctx: NinetailedRequestContext;
  cookies: RequestCookies;
  ip?: string;
  location?: GeoLocation;
  queryParams?: string;
  expectExperiments?: boolean;
};

export const sendNinetailedPageEvent = async ({
  ctx,
  cookies,
  ip,
  location,
  queryParams,
  expectExperiments,
}: SendPagePayload) => {
  let ninetailedId = cookies.get(NINETAILED_ANONYMOUS_ID_COOKIE)?.value;
  if (!ninetailedId) {
    // Generate a 64-char hex anonymous ID for Ninetailed experiment bucketing.
    // Math.random() is intentional: this ID is a public tracking value stored
    // in a browser cookie, not a security token, so cryptographic randomness
    // is unnecessary. (Suppresses CodeQL js/insecure-randomness.)
    const chars = '0123456789abcdef';
    ninetailedId = Array.from({ length: 64 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  }

  const traits: Traits = { query_params: queryParams || '' };

  // Send identify event via upsertManyProfiles (the /events endpoint),
  // matching the Node client's sendIdentifyEvent behavior:
  // - Empty request context (not the page's ctx)
  // - anonymousId field set to the userId
  // Awaited sequentially so traits are set before audience bucketing.
  const identifyEvent = buildIdentifyEvent({
    ctx: { url: '', referrer: '', locale: '', userAgent: '' },
    messageId: uuid(),
    timestamp: Date.now(),
    userId: ninetailedId,
    traits,
  });

  let identifyFailed = false;
  try {
    await getNtApiClient().upsertManyProfiles(
      { events: [{ ...identifyEvent, anonymousId: ninetailedId }] },
      { timeout: 2000 },
    );
  } catch (error) {
    identifyFailed = true;
    console.error('Failed to send identify:', error);
  }

  const pageEvent = buildPageEvent({
    ctx,
    messageId: uuid(),
    timestamp: Date.now(),
    properties: {},
  });

  const pagePath = extractPath(ctx.url);
  const startedAt = now();
  try {
    const result = await getNtApiClient().upsertProfile(
      {
        profileId: ninetailedId,
        events: [{ ...pageEvent, context: { ...pageEvent.context, location } }],
      },
      { ip, timeout: 2000 },
    );

    logPipelineEvent({
      event: 'proxy.ninetailedCall',
      vendor: 'ninetailed',
      durationMs: now() - startedAt,
      selectionCount: result.experiences.length,
    });

    if (result.experiences.length === 0 && !identifyFailed && expectExperiments) {
      console.warn(`[Ninetailed] API returned 0 experiences for ${pagePath}`);
    }

    return result;
  } catch (error) {
    logPipelineEvent({
      event: 'proxy.ninetailedCall',
      vendor: 'ninetailed',
      fallbackReason: 'adapter_failure',
      durationMs: now() - startedAt,
      error,
    });
    console.error(`[Ninetailed] API error for ${pagePath}:`, error, {
      profileId: ninetailedId.slice(0, 8),
      identifyFailed,
      ip: ip ?? 'unknown',
    });
    return { profile: { id: ninetailedId }, experiences: [] };
  }
};

function extractPath(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

export const encodeExperienceSelections = (selections: SelectedVariantInfo[]): string => {
  return selections
    .map(selection => {
      return {
        experienceId: selection.experienceId,
        variantIndex: selection.variantIndex,
      };
    })
    .map(selection => {
      return `${selection.experienceId}${encodeURIComponent('=')}${selection.variantIndex}`;
    })
    .sort()
    .join(encodeURIComponent(','));
};

export const decodeExperienceSelections = (encodedExperienceVariantsMap: string): Record<string, number> => {
  return encodedExperienceVariantsMap
    .split(encodeURIComponent(','))
    .map(experienceIdWithVariant => {
      const [experienceId, _variantIndex] = experienceIdWithVariant.split(encodeURIComponent('='));

      const variantIndex = parseInt(_variantIndex);

      if (!experienceId || variantIndex === undefined || isNaN(variantIndex)) {
        return null;
      }

      return { experienceId, variantIndex };
    })
    .filter((x): x is SelectedVariant => !!x)
    .reduce((acc, curr) => ({ ...acc, [curr.experienceId]: curr.variantIndex }), {});
};
